import Contexts from "../../../contexts/Contexts";
import HomeIcon from "../../Icon/HomeIcon";
import PeopleIcon from "../../Icon/PeopleIcon";
import TicketIcon from "../../Icon/TicketIcon";
import PerfilIcon from "../../Icon/PerfilIcon";
import styles from "./FloatNav.module.css";
import React, { useState, useRef, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar } from "@mui/joy";

/**
 * Bundle 5c.A — FloatNav restructure.
 *
 * Items: Início · Grupos · Apostas · Perfil
 * - DESAFIO removed from FloatNav (in-app Desafio entry points hidden in commit 1fc8c1a; /challenge route retained)
 * - Labels: sentence case (Brazilian convention, not ALL CAPS)
 * - Perfil item: shows user avatar (circular) when logged in, silhouette when logged out
 */
const FloatNav: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const navRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isNavOpen, screenSize, storefront, user, userId } = useContext(Contexts);
  const { t } = useTranslation();

  const loggedIn = !!(userId && userId !== 0 && !user?.isGuest);
  const userAvatar = user?.avatar || null;

  useEffect(() => {
    if (navRef.current && sliderRef.current) {
      const navItems = navRef.current.querySelectorAll(`.${styles.navItem}`);
      if (activeIndex < navItems.length) {
        const activeItem = navItems[activeIndex] as HTMLElement;
        const containerRect = navRef.current.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();
        if (screenSize.isDesktop) {
          const x = itemRect.top + itemRect.height / 2 - containerRect.top - 30;
          sliderRef.current.style.transform = `translateY(${x}px) translateX(-50%)`;
        } else {
          const x = itemRect.left + itemRect.width / 2 - containerRect.left - 30;
          sliderRef.current.style.transform = `translateX(${x}px) translateY(-50%)`;
        }
      }
    }
  }, [activeIndex, screenSize.isDesktop]);

  const handleNavItemClick = (index: number, path?: string) => {
    setActiveIndex(index);
    navigate(path || "/");
  };

  const location = useLocation();

  const navItems = [
    { title: t("nav.games"),   path: "/games",     icon: <HomeIcon /> },
    { title: t("nav.groups"),  path: "/groups",    icon: <PeopleIcon /> },
    { title: t("nav.tickets"), path: "/my-tickets", icon: <TicketIcon /> },
    {
      title: t("nav.perfil"),
      path: "/perfil",
      icon: loggedIn && userAvatar
        ? (
          <Avatar
            src={userAvatar}
            size="sm"
            sx={{
              width: 24,
              height: 24,
              border: "none",
              "--Avatar-size": "24px",
            }}
          />
        )
        : <PerfilIcon />,
    },
  ];

  useEffect(() => {
    const matchIndex = navItems.findIndex(item =>
      location.pathname === item.path || location.pathname.startsWith(item.path + "/")
    );
    setActiveIndex(matchIndex !== -1 ? matchIndex : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // On desktop, sidebar must clear the fixed header (67px) + affiliate ribbon (40px if present)
  const sidebarTop = screenSize.isDesktop ? (storefront ? 107 : 80) : undefined;

  return (
    <div
      className={`${styles.floatNav} ${
        isNavOpen || screenSize.isDesktop ? "" : styles.closed
      }`}
      style={sidebarTop !== undefined ? { top: sidebarTop } : undefined}
    >
      <div ref={navRef} className={styles.floatNavContent}>
        {navItems.map((item, index) => (
          <button
            key={index}
            className={`${styles.navItem} ${activeIndex === index ? styles.active : ""}`}
            onClick={() => handleNavItemClick(index, item.path)}
            aria-label={item.title}
            aria-current={activeIndex === index ? "page" : undefined}
          >
            {item.icon}
            <span>{item.title}</span>
          </button>
        ))}
        <div ref={sliderRef} className={styles.slider}></div>
      </div>
    </div>
  );
};

export default FloatNav;
